/**
 * Coach Notes — CRUD for org-scoped coach notes on athletes.
 *
 * Doctrine: ROLE_ARCHITECTURE.md
 * - Coach role is observational, per-org, requires member consent
 * - Notes have zero effect on scoring, matchmaking, or eligibility
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('COACH_NOTES');

export interface CoachNote {
  id: string;
  org_id: string | null;
  coach_user_id: string;
  athlete_user_id: string;
  note: string;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch all notes a coach has written about a specific athlete.
 * RLS ensures only the coach or athlete can read these.
 */
export async function fetchCoachNotes(
  coachUserId: string,
  athleteUserId: string,
): Promise<CoachNote[]> {
  const { data, error } = await supabase
    .from('coach_notes')
    .select('*')
    .eq('coach_user_id', coachUserId)
    .eq('athlete_user_id', athleteUserId)
    .order('created_at', { ascending: false });

  if (error) {
    log.warn('Failed to fetch coach notes', error.message);
    return [];
  }
  return (data as CoachNote[]) ?? [];
}

/**
 * Add a new note for an athlete.
 * Requires an active coach_athlete_link in the same org (enforced by RLS).
 */
export async function addCoachNote(
  orgId: string | null,
  coachUserId: string,
  athleteUserId: string,
  note: string,
): Promise<CoachNote | null> {
  const trimmed = note.trim();
  if (!trimmed) return null;

  const { data, error } = await supabase
    .from('coach_notes')
    .insert({
      ...(orgId ? { org_id: orgId } : {}),
      coach_user_id: coachUserId,
      athlete_user_id: athleteUserId,
      note: trimmed,
    })
    .select()
    .single();

  if (error) {
    log.error('Failed to add coach note', error.message);
    return null;
  }
  return data as CoachNote;
}

/**
 * Update an existing note's text. RLS ensures only the author can update.
 */
export async function updateCoachNote(noteId: string, note: string): Promise<boolean> {
  const trimmed = note.trim();
  if (!trimmed) return false;

  const { error } = await supabase
    .from('coach_notes')
    .update({ note: trimmed, updated_at: new Date().toISOString() })
    .eq('id', noteId);

  if (error) {
    log.error('Failed to update coach note', error.message);
    return false;
  }
  return true;
}

/**
 * Delete a note by ID. RLS ensures only the author can delete.
 */
export async function deleteCoachNote(noteId: string): Promise<boolean> {
  const { error } = await supabase
    .from('coach_notes')
    .delete()
    .eq('id', noteId);

  if (error) {
    log.error('Failed to delete coach note', error.message);
    return false;
  }
  return true;
}

/**
 * Mark a coach note as read by the athlete.
 */
export async function markNoteRead(noteId: string, athleteUserId: string): Promise<boolean> {
  const { error } = await supabase
    .from('coach_notes')
    .update({ read_at: new Date().toISOString(), read_by: athleteUserId })
    .eq('id', noteId);

  if (error) {
    log.warn('Failed to mark note as read', error.message);
    return false;
  }
  return true;
}

/**
 * Get unread note count for an athlete (notes from their coach they haven't read).
 */
export async function getUnreadNoteCount(athleteUserId: string): Promise<number> {
  const { count, error } = await supabase
    .from('coach_notes')
    .select('id', { count: 'exact', head: true })
    .eq('athlete_user_id', athleteUserId)
    .is('read_at', null);

  if (error) {
    log.warn('Failed to get unread count', error.message);
    return 0;
  }
  return count ?? 0;
}
